import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Image,
    Input,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import FormErrorMessageWithSpacing from '../../../components/form-error-message';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { ArticleDto } from '../../../models/article/dto/article.dto';
import { CreateArticleInputDto } from '../../../models/article/dto/create-article.dto';
import { CreateArticleWithImageInput, useCreateArticle } from '../hooks/useCreateArticle';
import { UpdateArticleWithImageInput, useUpdateArticle } from '../hooks/useUpdateArticle';

const defaultValues: CreateArticleInputDto = {
    title: '',
    content: '',
    imageUrl: '',
    imageUrls: [],
    isPopular: false,
    isPublished: true,
};

interface CreateArticleFormProps {
    article?: ArticleDto;
    isOpen?: boolean;
    onClose?: () => void;
}

export default function CreateArticleForm({ article, isOpen: externalIsOpen, onClose: externalOnClose }: CreateArticleFormProps = {}) {
    const { mutateAsync: createArticle, isLoading: isCreating } = useCreateArticle();
    const { mutateAsync: updateArticle, isLoading: isUpdating } = useUpdateArticle();
    const [formData, setFormData] = useState<CreateArticleInputDto>(defaultValues);
    const [errors, setErrors] = useState<Partial<Record<keyof CreateArticleInputDto, string>>>({});
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: externalIsOpen });
    const dropzoneBorderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
    const dropzoneHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
    const imageBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

    useEffect(() => {
        if (externalIsOpen !== undefined) {
            if (externalIsOpen) {
                onOpen();
            } else {
                onClose();
            }
        }
    }, [externalIsOpen, onOpen, onClose]);

    useEffect(() => {
        if (article) {
            setFormData({
                title: article.title,
                content: article.content,
                imageUrl: article.imageUrl,
                imageUrls: article.imageUrls,
                isPopular: article.isPopular,
                isPublished: article.isPublished,
            });
            // Set image preview if article has an imageUrl
            if (article.imageUrl) {
                setImagePreview(article.imageUrl);
            }
        } else {
            setFormData(defaultValues);
            setSelectedImage(null);
            setImagePreview(null);
        }
    }, [article]);

    const handleInputChange = (field: keyof CreateArticleInputDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Also clear imageUrl from formData if it exists
        setFormData(prev => ({ ...prev, imageUrl: undefined }));
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const validate = (): boolean => {
        const newErrors: Partial<Record<keyof CreateArticleInputDto, string>> = {};
        if (!formData.title.trim()) {
            newErrors.title = translate('article.title_required') || 'Title is required';
        }
        if (!formData.content.trim()) {
            newErrors.content = translate('article.content_required') || 'Content is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            if (article) {
                // Use image upload format if image file is selected, otherwise use regular format
                if (selectedImage) {
                    const updateData: UpdateArticleWithImageInput = {
                        articleData: {
                            title: formData.title,
                            content: formData.content,
                            imageUrls: formData.imageUrls,
                            isPopular: formData.isPopular,
                            isPublished: formData.isPublished,
                        },
                        imageFile: selectedImage,
                    };
                    await updateArticle({ id: article.id, input: updateData });
                } else {
                    await updateArticle({ id: article.id, input: formData });
                }
                toast({
                    title: translate('article.update_success') || 'Article updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                // Use image upload format if image file is selected, otherwise use regular format
                if (selectedImage) {
                    const createData: CreateArticleWithImageInput = {
                        articleData: {
                            title: formData.title,
                            content: formData.content,
                            imageUrls: formData.imageUrls,
                            isPopular: formData.isPopular,
                            isPublished: formData.isPublished,
                        },
                        imageFile: selectedImage,
                    };
                    await createArticle(createData);
                } else {
                    await createArticle(formData);
                }
                toast({
                    title: translate('article.create_success') || 'Article created successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }
            setFormData(defaultValues);
            setSelectedImage(null);
            setImagePreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            onClose();
            externalOnClose?.();
        } catch (error: any) {
            toast({
                title: translate('article.error') || 'Error',
                description: error.message || 'An error occurred',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleClose = () => {
        setFormData(defaultValues);
        setErrors({});
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
        externalOnClose?.();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            size="xl"
            title={article ? translate('article.edit') || 'Edit Article' : translate('article.create') || 'Create Article'}
            onSubmit={handleSubmit}
            submitLabel={article ? translate('common.save') || 'Save' : translate('common.create') || 'Create'}
            cancelLabel={translate('common.cancel') || 'Cancel'}
            isSubmitting={isCreating || isUpdating}
        >
                    <FormStack>
                        <FormControl isInvalid={!!errors.title}>
                            <FormLabel>{translate('article.title') || 'Title'}</FormLabel>
                            <Input
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                placeholder={translate('article.title_placeholder') || 'Enter article title'}
                            />
                            <FormErrorMessageWithSpacing title={errors.title || ''} />
                        </FormControl>

                        <FormControl isInvalid={!!errors.content}>
                            <FormLabel>{translate('article.content') || 'Content'}</FormLabel>
                            <Textarea
                                value={formData.content}
                                onChange={(e) => handleInputChange('content', e.target.value)}
                                placeholder={translate('article.content_placeholder') || 'Enter article content'}
                                rows={10}
                            />
                            <FormErrorMessageWithSpacing title={errors.content || ''} />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{translate('article.image_url') || 'Article Image'}</FormLabel>
                            <VStack align="start" spacing={3}>
                                <Input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    display="none"
                                />
                                <Box
                                    width="100%"
                                    p={4}
                                    border="2px dashed"
                                    borderColor={dropzoneBorderColor}
                                    borderRadius="md"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    _hover={{ borderColor: 'blue.400', bg: dropzoneHoverBg }}
                                    cursor="pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Text textAlign="center" color={subtleTextColor}>
                                        {translate('article.image_drop_hint') || 'Drag & drop an image here, or click to select'}
                                    </Text>
                                </Box>
                                <HStack>
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        variant="outline"
                                        size="sm"
                                    >
                                        {translate('article.select_image') || 'Select Image'}
                                    </Button>
                                    {(selectedImage || (article && article.imageUrl)) && (
                                        <Button
                                            onClick={handleRemoveImage}
                                            variant="ghost"
                                            size="sm"
                                            colorScheme="red"
                                        >
                                            {translate('common.remove') || 'Remove'}
                                        </Button>
                                    )}
                                </HStack>
                                {imagePreview && (
                                    <Box>
                                        <Text fontSize="sm" mb={2}>
                                            {translate('article.preview') || 'Preview:'}
                                        </Text>
                                        <Image
                                            src={imagePreview}
                                            alt="Article preview"
                                            maxW="200px"
                                            maxH="200px"
                                            objectFit="cover"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor={imageBorderColor}
                                        />
                                    </Box>
                                )}
                                {selectedImage && (
                                    <Text fontSize="sm" color={subtleTextColor}>
                                        {translate('article.selected_file') || 'Selected'}: {selectedImage.name}
                                    </Text>
                                )}
                            </VStack>
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb={0}>{translate('article.popular') || 'Popular'}</FormLabel>
                            <Switch
                                isChecked={formData.isPopular}
                                onChange={(e) => handleInputChange('isPopular', e.target.checked)}
                            />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb={0}>{translate('article.published') || 'Published'}</FormLabel>
                            <Switch
                                isChecked={formData.isPublished ?? true}
                                onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                            />
                        </FormControl>
                    </FormStack>
        </FormModal>
    );
}
